import type { Movie, DiscoverResponse, Filters, MediaType, MediaItem, TVShow } from '../types/movie';
import { TV_GENRE_KEYWORD_MAP, UNIFIED_GENRES, TV_ONLY_GENRES } from '../utils/constants';

// Worker API base URL - will be replaced with actual Cloudflare Worker URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Build genre and keyword params for TV discover
function buildTVGenreParams(selectedGenres: number[]): { genres: string | undefined; keywords: string | undefined } {
  const genreIds: number[] = [];
  const keywordIds: number[] = [];

  selectedGenres.forEach(genreId => {
    // Check if this genre needs keyword workaround
    if (TV_GENRE_KEYWORD_MAP[genreId]) {
      keywordIds.push(...TV_GENRE_KEYWORD_MAP[genreId]);
    } else {
      // Find the TV equivalent genre ID
      const unifiedGenre = [...UNIFIED_GENRES, ...TV_ONLY_GENRES].find(g => g.id === genreId);
      if (unifiedGenre && typeof unifiedGenre.tvId === 'number') {
        genreIds.push(unifiedGenre.tvId);
      } else {
        // Fallback: use the genre ID directly if it's a native TV genre
        genreIds.push(genreId);
      }
    }
  });

  return {
    genres: genreIds.length > 0 ? genreIds.join('|') : undefined,
    keywords: keywordIds.length > 0 ? keywordIds.join('|') : undefined,
  };
}

interface FetchOptions {
  endpoint: string;
  params?: Record<string, string | number | boolean | undefined>;
}

async function fetchFromAPI<T>({ endpoint, params = {} }: FetchOptions): Promise<T> {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const url = `${API_BASE_URL}${endpoint}?${searchParams.toString()}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Helper to convert TV response to unified MediaItem format
function normalizeToMediaItem(item: Movie | TVShow, mediaType: MediaType): MediaItem {
  if (mediaType === 'tv') {
    const tv = item as TVShow;
    return {
      id: tv.id,
      title: tv.name,
      overview: tv.overview,
      poster_path: tv.poster_path,
      backdrop_path: tv.backdrop_path,
      release_date: tv.first_air_date || '',
      genre_ids: tv.genre_ids || [],
      vote_average: tv.vote_average,
      vote_count: tv.vote_count,
      popularity: tv.popularity,
      original_language: tv.original_language,
      runtime: tv.episode_run_time?.[0], // Use first episode runtime
      media_type: 'tv',
      imdb_rating: tv.imdb_rating,
      rt_rating: tv.rt_rating,
      number_of_seasons: tv.number_of_seasons,
    };
  }
  const movie = item as Movie;
  return {
    ...movie,
    media_type: 'movie',
  };
}

export interface MediaDiscoverResponse {
  page: number;
  results: MediaItem[];
  total_pages: number;
  total_results: number;
}

export async function discoverMedia(
  filters: Partial<Filters>,
  page = 1,
  mediaType: MediaType = 'movie'
): Promise<MediaDiscoverResponse> {
  const params: Record<string, string | number | undefined> = {
    page,
    sort_by: filters.sortBy,
    'vote_count.gte': 50, // Minimum votes for quality results
  };

  // Genre filter - handle TV genre/keyword mapping
  if (filters.genres && filters.genres.length > 0) {
    if (mediaType === 'tv') {
      // Use keyword workaround for genres that don't exist natively in TV
      const { genres, keywords } = buildTVGenreParams(filters.genres);
      if (genres) {
        params.with_genres = genres;
      }
      if (keywords) {
        params.with_keywords = keywords;
      }
    } else {
      params.with_genres = filters.genres.join(',');
    }
  }

  // Year range - different params for TV vs Movie
  if (filters.yearRange) {
    if (mediaType === 'movie') {
      params['primary_release_date.gte'] = `${filters.yearRange[0]}-01-01`;
      params['primary_release_date.lte'] = `${filters.yearRange[1]}-12-31`;
    } else {
      params['first_air_date.gte'] = `${filters.yearRange[0]}-01-01`;
      params['first_air_date.lte'] = `${filters.yearRange[1]}-12-31`;
    }
  }

  // Runtime range (for movies) / Episode runtime for TV
  if (filters.runtimeRange) {
    if (filters.runtimeRange[0] > 0) {
      params['with_runtime.gte'] = filters.runtimeRange[0];
    }
    if (filters.runtimeRange[1] < 300) {
      params['with_runtime.lte'] = filters.runtimeRange[1];
    }
  }

  // TMDB rating range
  if (filters.tmdbRatingRange) {
    if (filters.tmdbRatingRange[0] > 0) {
      params['vote_average.gte'] = filters.tmdbRatingRange[0];
    }
    if (filters.tmdbRatingRange[1] < 10) {
      params['vote_average.lte'] = filters.tmdbRatingRange[1];
    }
  }

  // Language filter
  if (filters.languages && filters.languages.length > 0) {
    params.with_original_language = filters.languages.join('|');
  }

  // Person search (actor or director via cast/crew)
  if (filters.actorSearch) {
    const people = await searchPeople(filters.actorSearch);
    if (people.length > 0) {
      params.with_cast = people[0].id;
    }
  }

  if (filters.directorSearch) {
    const people = await searchPeople(filters.directorSearch);
    if (people.length > 0) {
      params.with_crew = people[0].id;
    }
  }

  const endpoint = mediaType === 'movie' ? '/discover/movie' : '/discover/tv';
  const response = await fetchFromAPI<DiscoverResponse>({
    endpoint,
    params,
  });

  return {
    ...response,
    results: response.results.map(item => normalizeToMediaItem(item, mediaType)),
  };
}

// Keep original function for backwards compatibility
export async function discoverMovies(
  filters: Partial<Filters>,
  page = 1
): Promise<DiscoverResponse> {
  const params: Record<string, string | number | undefined> = {
    page,
    sort_by: filters.sortBy,
    'vote_count.gte': 50, // Minimum votes for quality results
  };

  // Genre filter
  if (filters.genres && filters.genres.length > 0) {
    params.with_genres = filters.genres.join(',');
  }

  // Year range
  if (filters.yearRange) {
    params['primary_release_date.gte'] = `${filters.yearRange[0]}-01-01`;
    params['primary_release_date.lte'] = `${filters.yearRange[1]}-12-31`;
  }

  // Runtime range
  if (filters.runtimeRange) {
    if (filters.runtimeRange[0] > 0) {
      params['with_runtime.gte'] = filters.runtimeRange[0];
    }
    if (filters.runtimeRange[1] < 300) {
      params['with_runtime.lte'] = filters.runtimeRange[1];
    }
  }

  // TMDB rating range
  if (filters.tmdbRatingRange) {
    if (filters.tmdbRatingRange[0] > 0) {
      params['vote_average.gte'] = filters.tmdbRatingRange[0];
    }
    if (filters.tmdbRatingRange[1] < 10) {
      params['vote_average.lte'] = filters.tmdbRatingRange[1];
    }
  }

  // Language filter
  if (filters.languages && filters.languages.length > 0) {
    params.with_original_language = filters.languages.join('|');
  }

  // Person search (actor or director via cast/crew)
  if (filters.actorSearch) {
    // First search for the person, then use their ID
    const people = await searchPeople(filters.actorSearch);
    if (people.length > 0) {
      params.with_cast = people[0].id;
    }
  }

  if (filters.directorSearch) {
    const people = await searchPeople(filters.directorSearch);
    if (people.length > 0) {
      params.with_crew = people[0].id;
    }
  }

  return fetchFromAPI<DiscoverResponse>({
    endpoint: '/discover/movie',
    params,
  });
}

export async function searchMedia(
  query: string,
  page = 1,
  mediaType: MediaType = 'movie'
): Promise<MediaDiscoverResponse> {
  const endpoint = mediaType === 'movie' ? '/search/movie' : '/search/tv';
  const response = await fetchFromAPI<DiscoverResponse>({
    endpoint,
    params: { query, page },
  });

  return {
    ...response,
    results: response.results.map(item => normalizeToMediaItem(item, mediaType)),
  };
}

export async function searchMovies(
  query: string,
  page = 1
): Promise<DiscoverResponse> {
  return fetchFromAPI<DiscoverResponse>({
    endpoint: '/search/movie',
    params: { query, page },
  });
}

interface Person {
  id: number;
  name: string;
  profile_path: string | null;
  known_for_department: string;
  popularity?: number;
}

interface PersonSearchResponse {
  results: Person[];
}

export async function searchPeople(query: string): Promise<Person[]> {
  if (!query.trim()) return [];

  const response = await fetchFromAPI<PersonSearchResponse>({
    endpoint: '/search/person',
    params: { query },
  });

  return response.results;
}

export async function getMovieDetails(movieId: number): Promise<Movie> {
  return fetchFromAPI<Movie>({
    endpoint: `/movie/${movieId}`,
    params: {
      append_to_response: 'credits,videos',
    },
  });
}

export async function getTVDetails(tvId: number): Promise<TVShow> {
  return fetchFromAPI<TVShow>({
    endpoint: `/tv/${tvId}`,
    params: {
      append_to_response: 'credits,videos',
    },
  });
}

export async function getMediaDetails(id: number, mediaType: MediaType): Promise<MediaItem> {
  if (mediaType === 'tv') {
    const tv = await getTVDetails(id);
    return normalizeToMediaItem(tv, 'tv');
  }
  const movie = await getMovieDetails(id);
  return normalizeToMediaItem(movie, 'movie');
}

export async function getMovieVideos(movieId: number): Promise<{ results: { id: string; key: string; name: string; site: string; type: string }[] }> {
  return fetchFromAPI({
    endpoint: `/movie/${movieId}/videos`,
  });
}

export interface PersonDetails {
  id: number;
  name: string;
  biography: string;
  birthday: string | null;
  deathday: string | null;
  place_of_birth: string | null;
  profile_path: string | null;
  known_for_department: string;
  also_known_as: string[];
  gender: number;
  popularity: number;
  combined_credits?: {
    cast: {
      id: number;
      title?: string;
      name?: string;
      character: string;
      release_date?: string;
      first_air_date?: string;
      poster_path: string | null;
      vote_average: number;
      media_type: 'movie' | 'tv';
    }[];
    crew: {
      id: number;
      title?: string;
      name?: string;
      job: string;
      release_date?: string;
      first_air_date?: string;
      poster_path: string | null;
      vote_average: number;
      media_type: 'movie' | 'tv';
    }[];
  };
}

export async function getPersonDetails(personId: number): Promise<PersonDetails> {
  return fetchFromAPI<PersonDetails>({
    endpoint: `/person/${personId}`,
    params: {
      append_to_response: 'combined_credits',
    },
  });
}

export async function getPopularMovies(page = 1): Promise<DiscoverResponse> {
  return fetchFromAPI<DiscoverResponse>({
    endpoint: '/movie/popular',
    params: { page },
  });
}

export async function getTrendingMovies(
  timeWindow: 'day' | 'week' = 'week'
): Promise<DiscoverResponse> {
  return fetchFromAPI<DiscoverResponse>({
    endpoint: `/trending/movie/${timeWindow}`,
  });
}
